import { useMutation, useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'

import { SearchParam } from '@models/search/SearchParam'
import { SearchRecommendationResponse } from '@models/recommend/SearchRecommendationResponse'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useBookAction } from '@hooks/book/useBookAction'
import { useSearchBookAction } from './useSearchBookAction'

export function useSearch(params: SearchParam) {
  const searchBookClient = useSearchBookAction()
  const { canFetchApi, isAuthenticated } = useAuthentication()
  const queryClient = useQueryClient()
  const loginModal = useModal({ modal: 'login' })
  const bookClient = useBookAction()
  const { data, isLoading: isLoadingLogin } = useQuery(
    ['search_book', params],
    () => searchBookClient.searchBook(params),
    { enabled: canFetchApi }
  )

  const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
    useMutation((id: number) => bookClient.toggleBookLike(id), {
      onSuccess: (type, id) => {
        queryClient.setQueryData<SearchRecommendationResponse | undefined>(
          ['search_book', params],
          (oldData?: SearchRecommendationResponse) => {
            if (oldData) {
              const temp = cloneDeep(oldData.data)
              const index = temp.findIndex(row => row.id === id)

              if (index !== -1) {
                temp[index].isLiked = type === LikeSuccessStatusEnum.CREATE
                temp[index].likeCount =
                  type === LikeSuccessStatusEnum.CREATE
                    ? temp[index].likeCount + 1
                    : Math.max(0, temp[index].likeCount - 1)
              }

              return {
                ...oldData,
                data: temp,
              }
            }

            return oldData
          }
        )
      },
    })

  async function handleLikeBook(id: number) {
    if (isAuthenticated) {
      await toggleBookLike(id)
    } else {
      loginModal.show()
    }
  }

  return {
    data,
    isLoading: isLoadingLogin,
    isLoadingToggleBookLike,
    handleLikeBook,
  }
}
